import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from "@angular/forms";
import { Subscription, Observable, of } from "rxjs";
import { tap, map } from "rxjs/operators";
import { GridOptions, ColDef, GridApi, RowSelectionOptions } from "ag-grid-community";

import { eDiscoveryRequestSearchSpec, eDiscoveryStatusSearchType, eDiscoveryRequest } from "../../../models/eDiscovery.model";
import { Organization } from "../../../../common/models/organization.model";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { EDiscoveryService } from "../../../services/eDiscovery.service";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { OrganizationService } from "../../../../common/services/organization.service";

@Component({
    selector: 'eDiscovery-request-search',
    templateUrl: './request-search.component.html',
    styleUrls: ['./request-search.component.scss', '../request-wrapper/request-wrapper.component.scss'],
    providers: [EDiscoveryService],
    standalone: false
})
export class EDiscoveryRequestSearchComponent implements OnInit {

    availableOrganizations: Organization[];
    availableOrgs$: Observable<Organization[]>;

    private _currentRequest: eDiscoveryRequestSearchSpec = new eDiscoveryRequestSearchSpec(eDiscoveryStatusSearchType.All, "");

    dummyRequest: eDiscoveryRequest = new eDiscoveryRequest("--Select a Request--");     

    @Output() errorOccurred: EventEmitter<any> = new EventEmitter<any>();
    @Output() searchParamsChanged: EventEmitter<eDiscoveryRequestSearchSpec> = new EventEmitter<eDiscoveryRequestSearchSpec>();
    existingRequests$: Subscription;
    existingRequests: eDiscoveryRequest[];
    filteredRequests: eDiscoveryRequest[];
	gridOptions: GridOptions;
	gridApi?: GridApi;
    notReadyDialog: Dialog = new Dialog("Please wait for search options to finish loading.", [new DialogButton("OK", true, () => { return false; })]);
    private _organization: Organization = null;
    @Input() set organization(value: Organization) {
        if (this._organization != value) {            
            this._organization = value;
            this.organizationChanged.emit(this._organization);
        }
    }

    get organization(): Organization {
        return this._organization;
    }
    @Output() organizationChanged: EventEmitter<Organization> = new EventEmitter<Organization>();
    @Output() orgFilterChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() runSearch: EventEmitter<eDiscoveryRequestSearchSpec> = new EventEmitter<eDiscoveryRequestSearchSpec>();
    searchForm: FormGroup;
    @Input() set searchRequest(value: eDiscoveryRequestSearchSpec) {
        if (!this._currentRequest || this._currentRequest.organizationId !== value.organizationId) {
            this._headerService.updateSubHeader(this.subheader);
        }
        this._currentRequest = value;
    }

    get searchRequest(): eDiscoveryRequestSearchSpec {
        return this._currentRequest;
    }



    searchStatusType: eDiscoveryStatusSearchType = eDiscoveryStatusSearchType.All;
    searchTypes = eDiscoveryStatusSearchType;
    @Output() startNewRequest: EventEmitter<Organization | null> = new EventEmitter<Organization | null>();

    subheader: SubHeader = new SubHeader({
        title: "eDiscovery",
        buttons: [{ name: "Search", method: () => this.startSearch()  }, { name: "Create New", method: () => this.confirmStartNew() }]
    });
    subscriptions = new Subscription();

    useExistingOrgDialog: Dialog = new Dialog("You currently have an organization selected.  Do you wish to create a request for the current organization?", [new DialogButton("No", true, () => this.startNew(false)), new DialogButton("Yes", true, () => this.startNew(true)), new DialogButton("I've changed my mind", false, () => this.onDialogClose())])

    constructor(private _headerService: SubHeaderService,
        private _dialogService: DynamicDialogService,
        private _discoveryService: EDiscoveryService,
        private _formatter: GridFormatterService,
        private _organizationService: OrganizationService,
		private _builder: FormBuilder) {
	}

    ngOnInit() {
        const orgChanged$ = this.organizationChanged.asObservable().pipe()
            .subscribe((org) => {
                if (org && org.id !== "") {
                    this.filteredRequests = this.existingRequests.filter(r => r.organizationId === org.id);
                    this.filteredRequests.unshift(this.dummyRequest);
                } else {
                    this.filteredRequests = this.existingRequests;
                    if (this.existingRequests.findIndex(ex => ex.requestId === this.dummyRequest.requestId) === -1) {
                        this.filteredRequests.unshift(this.dummyRequest);
                    }
                }                            
                if (this.filteredRequests.findIndex(req => req.requestId === this._currentRequest.requestId) === -1) {
                    this._currentRequest.requestId = this.dummyRequest.requestId;
                }
            });
        this.subscriptions.add(orgChanged$);
        this.buildSearchForm();
        this._headerService.updateSubHeader(this.subheader);
        this.gridOptions = {
            columnDefs: this.defineColumns(),
            defaultColDef: { filter: true, sortable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            onGridReady: (params) => {
				this.gridApi = params.api;
				this.gridApi.sizeColumnsToFit();
            },
            suppressHorizontalScroll: true,
            pagination: true,
            paginationAutoPageSize: true,
            rowSelection: {
                mode: "singleRow"                
            } as RowSelectionOptions
        } as GridOptions;

        this.availableOrgs$ = this._organizationService.getAll().pipe(
            map((data: Organization[]) => {
                this.availableOrganizations = data.filter(o => o.name !== 'ODNR').sort((a, b) => {
                    if (a.name > b.name) return 1;
                    if (a.name < b.name) return -1;
                    return 0;
                });
                if (this.gridApi != null && this.organization != null) {
                    let orgId = this.organization.id;
                    setTimeout((orgId: any) => {
                       if (this.gridApi != null) {
                         this.gridApi.forEachNode((node) => {
                            node.setSelected(node.data.id === orgId);
                        });
                       }
                    }, 500, orgId);
                }
                return this.availableOrganizations;
            })
        );
    }

    ngOnDestroy() {
        if (this.existingRequests$) this.existingRequests$.unsubscribe();
        if (this.subscriptions) this.subscriptions.unsubscribe();
    }

    buildSearchForm(): void {      
        if (!this.existingRequests$) {
            const existingSearch = new eDiscoveryRequestSearchSpec(eDiscoveryStatusSearchType.All, "");            
            this.existingRequests$ = this._discoveryService.searchRequests(existingSearch).pipe(
                tap((data) => {
                    data.filter(d => d != null).forEach(d => d.displayText = d.requestId);
                    this.existingRequests = data;
                    this.filteredRequests = (this.organization) ? this.existingRequests.filter(r => r.organizationId === this.organization.id) : this.existingRequests;
                    this.filteredRequests.unshift(this.dummyRequest);
                    this._currentRequest.requestId = this.dummyRequest.requestId;
                })
            ).subscribe({
                next: () => { },
                error: (error) => {
                    console.error(error);
                    this.errorOccurred.emit(error);
                }
            });
            this.subscriptions.add(this.existingRequests$);
        }
        this.searchForm = this._builder.group({
            orgSelector: [this._currentRequest.organizationId, []],
            requestSelector: [this._currentRequest.requestId, []],
            statusType: [this._currentRequest.statusType, []],
            activeOrgs: [true, []]
        });

       
    }

    confirmStartNew(): void {
        if (this.organization && this.organization.id !== "") {
            this.useExistingOrgDialog.toggleDialog(true);
            this._dialogService.updateDialog(this.useExistingOrgDialog);
        }
        else {
            this.startNew(false);
        }

    }

    defineColumns(): ColDef[] {
        const columnDefs = [
            {
                field: "name",
                headerName: "Organization Name",
                headerTooltip: "Organization Name",
                maxWidth: 500,
                tooltipField: "name",
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams
            } as ColDef,
            {
                field: "id",
                headerName: "RICS Owner Number",
                headerTooltip: "RICS Owner Number",
                tooltipField: "id"
            } as ColDef,
            {
                field: "historicalOwnerNumber",
                headerName: "RBDMS Owner Number",
                headerTooltip: "RBDMS Owner Number",
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams
            }
        ] as ColDef[];
        this._formatter.applyFilterKeyboardHintTooltips(columnDefs);
        return columnDefs;
    }

    disableSearchButton(): boolean {
        return this.availableOrganizations.length === 0;
    }

    newSearch(): void {
        this.searchRequest = new eDiscoveryRequestSearchSpec(eDiscoveryStatusSearchType.All, "");
        this.organization = null;
        this.buildSearchForm();
    }

    onDialogClose(): void {
        
    }

    orgSelectionChanged(evt: any): void {
        const tempSelected: Organization[] = [];
        const rows = this.gridApi.getSelectedRows();
        let orgId = "";
        switch (rows.length) {
            case 0:
                orgId = "";
                break;
            case 1:
                orgId = rows[0].id;
                break;
            default:
                console.error("somehow have more than one selected row in the grid");
                return;
                break;
        }
        let orgCtrl = this.searchForm.get("orgSelector");
        if (orgCtrl != null) {
            orgCtrl.setValue(orgId);
        }
        this.validateSearchForm();
    }

    startNew(useExisting: boolean): void {
        if (useExisting) {
            this.startNewRequest.emit(this.organization);
        } else {
            this.startNewRequest.emit(null);
        }        
    }

    startSearch(): void {
        if (!this.availableOrganizations || this.availableOrganizations.length === 0) {
            this.notReadyDialog.toggleDialog(true);
            this._dialogService.updateDialog(this.notReadyDialog);
        } else {
            this.organization = null;
            if (this.searchRequest.organizationId !== "") {
                let org = this.availableOrganizations.find(o => o.id === this.searchRequest.organizationId);
                if (org) {
                    this.organization = org;
                }
            }
            switch (this.searchRequest.statusType) {
                case eDiscoveryStatusSearchType.All:
                    this.searchStatusType = eDiscoveryStatusSearchType.All;
                    break;
                case eDiscoveryStatusSearchType.Completed:
                    this.searchStatusType = eDiscoveryStatusSearchType.Completed;
                    break;
                case eDiscoveryStatusSearchType.Pending:
                    this.searchStatusType = eDiscoveryStatusSearchType.Pending;
                    break;

            }
            this.searchRequest.statusType = this.searchStatusType;
            this.runSearch.emit(this.searchRequest);
        }       
    }

   

    validateSearchForm(): void {
        this.searchForm.controls["orgSelector"].updateValueAndValidity();
        this.searchForm.controls["requestSelector"].updateValueAndValidity();
        this.searchForm.controls["statusType"].updateValueAndValidity();
        const changed = (this.searchForm.controls["orgSelector"].value !== this._currentRequest.organizationId)
            || (this.searchForm.controls["requestSelector"].value !== this._currentRequest.requestId)
            || (this.searchForm.controls["statusType"].value !== this._currentRequest.statusType);

        
        if (this.searchForm.controls["orgSelector"].value !== this._currentRequest.organizationId) {
            this.organization = this.availableOrganizations.find(o => o.id === this.searchForm.controls["orgSelector"].value);
            this._currentRequest.organizationId = this.searchForm.controls["orgSelector"].value; 
        }
        
        
        if (this.searchForm.controls["statusType"].value) {           
            this._currentRequest.statusType = this.searchForm.controls["statusType"].value;
        }
        this._currentRequest.requestId = this.searchForm.controls["requestSelector"].value;
        if (changed) this.searchParamsChanged.emit(this._currentRequest);
    }
}
